// MIRRORS: panel/components/toast/toast.tsx — identical copy; edit both, or delete this line on both to declare a deliberate split.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2 } from 'lucide-react';
import styles from './toast.module.scss';

const VISIBLE_MS = 3000;

/**
 * One transient message at a time, replaced by the next and gone in three seconds.
 * Never for an error the user must act on: that stays inline, where it cannot time out.
 */
export function Toast({
  message,
  onDismiss,
  icon,
  tone = 'success',
  placement = 'bottom',
}: {
  message: string | null;
  onDismiss: () => void;
  /** Defaults to the success tick. Pass one when the message is not a success. */
  icon?: ReactNode;
  /** Red for a dead end, orange for something that changed under the user. */
  tone?: 'success' | 'warning' | 'caution';
  /** "inline" hangs the toast from the caller's positioned ancestor instead of the page. */
  placement?: 'bottom' | 'inline';
}) {
  // `shown` lags `message` by one frame so the element mounts hidden and fades in.
  const [shown, setShown] = useState(false);
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;

  useEffect(() => {
    if (!message) {
      setShown(false);
      return;
    }
    const raf = requestAnimationFrame(() => setShown(true));
    const timer = window.setTimeout(() => {
      setShown(false);
      // Cleared after the fade, so the text does not pop out mid-transition.
      window.setTimeout(() => dismissRef.current(), 200);
    }, VISIBLE_MS);

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(timer);
    };
    // Keyed on the message, so a second one restarts the countdown.
  }, [message]);

  if (!message) return null;

  const node = (
    <div
      // polite: it reports what just happened and must not interrupt reading.
      role="status"
      aria-live="polite"
      className={`${styles.toast} ${styles[tone]} ${styles[placement]} ${
        shown ? styles.shown : ''
      }`}
    >
      {icon ?? <CheckCircle2 aria-hidden />}
      <span>{message}</span>
    </div>
  );

  return placement === 'inline' ? node : createPortal(node, document.body);
}

export type ToastNode = ReactNode;
